import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  XMarkIcon,
  ArrowDownTrayIcon,
  ArrowTopRightOnSquareIcon,
  ExclamationCircleIcon,
  DocumentArrowDownIcon,
} from '@heroicons/react/24/outline';

interface PdfViewerModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Presigned S3 URL. null = still fetching the URL from the API. */
  url: string | null;
  title?: string;
  error?: string | null;
  downloadFileName?: string;
}

type ViewState = 'awaiting-url' | 'ready' | 'fetching' | 'loaded' | 'error';

const PdfViewerModal: React.FC<PdfViewerModalProps> = ({
  isOpen,
  onClose,
  url,
  title = 'Certificate',
  error = null,
  downloadFileName,
}) => {
  const backdropRef = useRef<HTMLDivElement>(null);
  const blobUrlRef = useRef<string | null>(null);

  const [viewState, setViewState] = useState<ViewState>('awaiting-url');
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [fetchError, setFetchError] = useState<string | null>(null);

  // Reset when modal opens or URL changes
  useEffect(() => {
    if (!isOpen) return;
    revokeBlobUrl();
    setBlobUrl(null);
    setFetchError(null);
    if (error) {
      setViewState('error');
    } else if (url) {
      setViewState('ready');
    } else {
      setViewState('awaiting-url');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, url, error]);

  // When modal closes, revoke any held blob URL
  useEffect(() => {
    if (!isOpen) revokeBlobUrl();
  }, [isOpen]);

  // Cleanup on unmount
  useEffect(() => () => revokeBlobUrl(), []);

  function revokeBlobUrl() {
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }
  }

  const handleLoad = useCallback(async () => {
    if (!url) return;
    setViewState('fetching');
    setFetchError(null);
    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      revokeBlobUrl();
      const objUrl = URL.createObjectURL(blob);
      blobUrlRef.current = objUrl;
      setBlobUrl(objUrl);
      setViewState('loaded');
    } catch (err) {
      setFetchError(err instanceof Error ? err.message : 'Failed to load PDF');
      setViewState('error');
    }
  }, [url]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  // Lock body scroll
  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === backdropRef.current) onClose();
  };

  return (
    <div
      ref={backdropRef}
      onClick={handleBackdropClick}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
    >
      <div className="relative flex flex-col w-full max-w-5xl h-[90vh] bg-white dark:bg-gray-900 rounded-xl shadow-2xl overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 flex-shrink-0">
          <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-200 truncate max-w-[60%]">
            {title}
          </h2>
          <div className="flex items-center gap-2">
            {url && (
              <>
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  title="Open in new tab"
                >
                  <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5" />
                  Open
                </a>
                <a
                  href={url}
                  download={downloadFileName}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <ArrowDownTrayIcon className="w-3.5 h-3.5" />
                  Download
                </a>
              </>
            )}
            <button
              type="button"
              onClick={onClose}
              className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
              title="Close"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 min-h-0 bg-gray-100 dark:bg-gray-950 flex items-center justify-center">

          {/* Waiting for API to return the URL */}
          {viewState === 'awaiting-url' && (
            <div className="flex flex-col items-center gap-3">
              <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
              <p className="text-sm text-gray-500 dark:text-gray-400">Fetching link…</p>
            </div>
          )}

          {/* URL ready — ask user to load */}
          {viewState === 'ready' && (
            <div className="flex flex-col items-center gap-4 text-center px-8">
              <DocumentArrowDownIcon className="w-12 h-12 text-gray-300 dark:text-gray-600" />
              <p className="text-sm text-gray-500 dark:text-gray-400">
                PDF is ready to view
              </p>
              <button
                type="button"
                onClick={handleLoad}
                className="flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
              >
                Load PDF
              </button>
            </div>
          )}

          {/* Fetching blob */}
          {viewState === 'fetching' && (
            <div className="flex flex-col items-center gap-3">
              <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
              <p className="text-sm text-gray-500 dark:text-gray-400">Loading PDF…</p>
            </div>
          )}

          {/* Loaded — show in iframe */}
          {viewState === 'loaded' && blobUrl && (
            <iframe
              src={blobUrl}
              title={title}
              className="w-full h-full border-0"
            />
          )}

          {/* Error */}
          {viewState === 'error' && (
            <div className="flex flex-col items-center gap-3 text-center px-8">
              <ExclamationCircleIcon className="w-10 h-10 text-red-400" />
              <p className="text-sm font-medium text-red-600 dark:text-red-400">
                {fetchError || error || 'Something went wrong'}
              </p>
              {url && (
                <button
                  type="button"
                  onClick={handleLoad}
                  className="mt-1 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Retry
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PdfViewerModal;
